import { useCallback, useEffect, useState } from "react";
import {
  mdiAttachment,
  mdiTrayArrowDown,
  mdiRefresh,
  mdiCloudDownloadOutline,
  mdiClose
} from "@mdi/js";
import Icon from "@mdi/react";

import { LoadingSpinnerSVG, VisibilitySVGIcon } from "components/SVGIcons";
import { IAttachment } from "models/HaulPay";
import styles from "./style.module.scss";
import { axios } from "services/middleware";
import { showErrorMessage } from "helpers/toastMessage";
import clsx from "clsx";

interface IAttachmentItemProps {
  label: string;
  data?: IAttachment | null;
  url?: string;
  fetchDocument?: () => Promise<any> | null;
}

export default function FRAttachment({
  label,
  data,
  url,
  fetchDocument
}: Readonly<IAttachmentItemProps>) {
  const [generatedAttachment, setGeneratedAttachment] = useState<IAttachment>(data as IAttachment);
  const [isFetching, setIsFetching] = useState(false);
  const [errorFetching, setErrorFetching] = useState(false);

  useEffect(() => {
    data?.download_url && setGeneratedAttachment(data);
  }, [data?.download_url]);

  const handlerGenerateAttachmentFromUrl = useCallback(async (url: string) => {
    try {
      setIsFetching(true);
      const { data } = await axios.get(url);
      setGeneratedAttachment(data);
    } catch (error) {
      showErrorMessage("Error creating File.");
      setErrorFetching(true);
    } finally {
      setIsFetching(false);
      setTimeout(() => setErrorFetching(false), 10000);
    }
  }, []);

  const handlerGenerateAttachmentFromFunction = useCallback(async () => {
    if (!fetchDocument) return;
    try {
      setIsFetching(true);
      const { data } = await fetchDocument();
      setGeneratedAttachment(data);
    } catch (error) {
      showErrorMessage("Error creating File.");
      setErrorFetching(true);
    } finally {
      setIsFetching(false);
      setTimeout(() => setErrorFetching(false), 10000);
    }
  }, []);

  return (
    <li className={styles.frAttachmentContainer}>
      <div className={styles.label}>
        <Icon path={mdiAttachment} className={styles.attachmentIcon} />
        <span>{label}</span>
      </div>
      {errorFetching ? (
        <a target="_blank" rel="noopener noreferrer" className={clsx(styles.button, "!bg-red-800")}>
          <Icon path={mdiClose} />
        </a>
      ) : !generatedAttachment?.preview_url || isFetching ? (
        <a
          onClick={() =>
            (url && handlerGenerateAttachmentFromUrl(url)) ||
            (fetchDocument && handlerGenerateAttachmentFromFunction())
          }
          target="_blank"
          rel="noopener noreferrer"
          className={styles.button}>
          {isFetching ? (
            <LoadingSpinnerSVG className="inline size-3 animate-spin text-text fill-primary mx-auto" />
          ) : (
            <Icon path={mdiCloudDownloadOutline} />
          )}
        </a>
      ) : (
        <>
          <a
            href={generatedAttachment?.preview_url}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.button}>
            <VisibilitySVGIcon />
          </a>
          <a
            href={generatedAttachment?.download_url}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.button}>
            <Icon path={mdiTrayArrowDown} />
          </a>
          {Boolean(url) && (
            <a
              onClick={() => url && handlerGenerateAttachmentFromUrl(url)}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.button}>
              <Icon path={mdiRefresh} />
            </a>
          )}
        </>
      )}
    </li>
  );
}
